// Session-scoped cache for AI Summary results. Keyed by
// `${interactionId}:${from}:${to}` so the same call reuses the cached
// summary across view switches, but a different search window regenerates.
//
// Backing store: sessionStorage. Survives tab navigation inside the SPA,
// cleared on tab close. We keep the cache tiny (max 20 entries, LRU
// eviction) so we never hit the ~5 MB sessionStorage quota.

const STORAGE_KEY = 'wxccOps:summaryCache:v1';
const MAX_ENTRIES = 20;

function readStore() {
  if (typeof sessionStorage === 'undefined') return { order: [], entries: {} };
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { order: [], entries: {} };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.order) || typeof parsed.entries !== 'object') {
      return { order: [], entries: {} };
    }
    return parsed;
  } catch {
    return { order: [], entries: {} };
  }
}

function writeStore(store) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Quota exceeded — silently drop. The cache is best-effort.
  }
}

function keyFor(interactionId, from, to) {
  return `${interactionId}:${from ?? ''}:${to ?? ''}`;
}

/**
 * Return the cached summary payload for a call, or null if not cached.
 * The payload matches what the summarizer Lambda returns: { summary,
 * modelId, durationMs, sourcesUsed }.
 */
export function getCachedSummary(interactionId, from, to) {
  if (!interactionId) return null;
  const store = readStore();
  const k = keyFor(interactionId, from, to);
  const hit = store.entries[k];
  if (!hit) return null;
  // Touch the LRU order so this entry stays warm.
  const nextOrder = [k, ...store.order.filter((x) => x !== k)];
  writeStore({ order: nextOrder, entries: store.entries });
  return hit;
}

/**
 * Cache a summary payload. If the cache is at capacity, the oldest entry
 * is evicted. If sessionStorage is full we silently fail — the cache is a
 * performance optimisation, not a correctness guarantee.
 */
export function setCachedSummary(interactionId, from, to, payload) {
  if (!interactionId || !payload) return;
  const store = readStore();
  const k = keyFor(interactionId, from, to);
  const entries = { ...store.entries, [k]: payload };
  const order = [k, ...store.order.filter((x) => x !== k)];
  while (order.length > MAX_ENTRIES) {
    const evicted = order.pop();
    delete entries[evicted];
  }
  writeStore({ order, entries });
}

export function clearSummaryCache() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
